import { apiGet, APIURL, appendFlatpickrBaseStyles, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, initFlatpickrInShadow, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';
import { format } from 'date-fns';

interface EsgScoreConfig extends WidgetConfig {
  dateRange?: string;
  onDateRangeChange?: (range: string) => void;
  availableDateRanges?: string[];
  enableCalendar?: boolean;
  onDateChange?: (startDate: string, endDate: string) => void;
}

export class TraderverseEsgScore {
  private container: HTMLElement;
  private config: EsgScoreConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private lastFetchedAt: Date | null = null;
  private startDate: string = '';
  private endDate: string = '';
  private selectedRange: string = 'Last 2 Years';
  private datePickerInstance: any = null;

  constructor(config: EsgScoreConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    if (config.dateRange) {
      this.selectedRange = config.dateRange;
    }
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private setDateRange(range: string): void {
    const today = new Date();
    let start = new Date();

    switch (range || 'Last 2 Years') {
      case 'Current Week':
        start.setDate(today.getDate() - today.getDay());
        break;
      case 'Last Week':
        start.setDate(today.getDate() - today.getDay() - 7);
        today.setDate(start.getDate() + 6);
        break;
      case 'Last 2 Weeks':
        start.setDate(today.getDate() - 14);
        break;
      case 'Last 3 Months':
        start.setMonth(today.getMonth() - 3);
        break;
      case 'This Month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'Last 1 Year':
        start.setFullYear(today.getFullYear() - 1);
        break;
      case 'Last 2 Years':
        start.setFullYear(today.getFullYear() - 2);
        break;
      default:
        start.setMonth(today.getMonth() - 3);
        break;
    }

    this.startDate = start.toISOString().split('T')[0];
    this.endDate = today.toISOString().split('T')[0];
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' },
    ];
  }

  private getDefaultConfig(): Partial<EsgScoreConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true,
      dateRange: 'Last 2 Years',
      availableDateRanges: [
        'Current Week',
        'Last Week',
        'Last 2 Weeks',
        'Last 3 Months',
        'This Month',
        'Last 1 Year',
        'Last 2 Years',
      ],
      enableCalendar: true,
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private filtersIconSvg(): string {
    return `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg>`;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;

    this.setDateRange(this.selectedRange);

    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.attachStyles();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');

    this.emit('ready', {});
    this.config.onReady?.();
    cancelSelectEvent(this.shadowRoot);
    setTimeout(() => this.initDatePicker(), 0);
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
    let dataTheme = 'default-dark';

    if (this.config.theme === 'white') {
      themeClass = 'whiteMode';
      dataTheme = 'white';
    } else if (this.config.theme === 'black') {
      themeClass = 'blackMode';
      dataTheme = 'black';
    } else if (this.config.theme === 'default-lite') {
      themeClass = 'defaultLightMode';
      dataTheme = 'default-lite';
    }

    const widgetHtml = `
      <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title stock">${this.getTitleIconSvg()}ESG Scores</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
            <tooltip-wrapper id="lastUpdatedTooltip">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.625 6.625C0.625 7.41293 0.780195 8.19315 1.08172 8.9211C1.38325 9.64905 1.82521 10.3105 2.38236 10.8676C2.93951 11.4248 3.60095 11.8667 4.3289 12.1683C5.05685 12.4698 5.83707 12.625 6.625 12.625C7.41293 12.625 8.19315 12.4698 8.9211 12.1683C9.64905 11.8667 10.3105 11.4248 10.8676 10.8676C11.4248 10.3105 11.8667 9.64905 12.1683 8.9211C12.4698 8.19315 12.625 7.41293 12.625 6.625C12.625 5.0337 11.9929 3.50758 10.8676 2.38236C9.74242 1.25714 8.2163 0.625 6.625 0.625C5.0337 0.625 3.50758 1.25714 2.38236 2.38236C1.25714 3.50758 0.625 5.0337 0.625 6.625Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M6.625 3.2915V6.62484L8.625 8.62484" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <tooltip><span>Last Updated</span><strong>N/A</strong></tooltip>
            </tooltip-wrapper>
            <div class="item">
              <button class="filters-toggle" type="button">${this.filtersIconSvg()}</button>
            </div>
            <i class="pi pi-ellipsis-v"></i>
          </div>
        </div>

        <div class="inBody">
          <div class="optionsRow">
            ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
              <div class="trader-sm-card" id="selected-symbol">
                ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: 'stock', name: '' })}
              </div>
              <span id="symbol-dropdown-placeholder"></span>
            </div>`}
            <span></span>
            <div class="filtersBox sideBar hidden">
              <div class="item">
                <select class="range-select">
                  ${(this.config.availableDateRanges || [
                    'Current Week',
                    'Last Week',
                    'Last 2 Weeks',
                    'Last 3 Months',
                    'This Month',
                    'Last 1 Year',
                    'Last 2 Years',
                  ])
                    .map(
                      (range) =>
                        `<option value="${range}"${range === this.selectedRange ? ' selected' : ''}>${range}</option>`
                    )
                    .join('')}
                </select>
              </div>
              ${
                this.config.enableCalendar !== false
                  ? `
              <div class="item date-picker">
                <input type="text" class="date-input" />
              </div>`
                  : ''
              }
            </div>
          </div>
          <div class="chartDv" id="eventsContainer"></div>
        </div>
      </div>
    `;

    this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);

    const filterBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
    if (filterBtn && filtersBox) {
      filterBtn.addEventListener('click', () => {
        filtersBox.classList.toggle('hidden');
      });
    }

    const rangeSelect = this.shadowRoot.querySelector('.range-select') as HTMLSelectElement;
    if (rangeSelect) {
      rangeSelect.addEventListener('change', () => {
        this.selectedRange = rangeSelect.value;
        this.setDateRange(this.selectedRange);
        this.setupChart(this.config.symbol!);
        this.initDatePicker();
        this.config.onDateRangeChange?.(this.selectedRange);
      });
    }

    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) {
        placeholder.replaceWith(dropdown);
      }
    }
  }

  onSelectChange(stock: any) {
    this.changeSymbol(stock.symbol, stock);
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(
      this.shadowRoot,
      this.config,
      stock,
      stockId,
      (stockId: any) => this.setupChart(stockId),
      (type: any, data: any) => this.emit(type, data)
    );
  }

  private initDatePicker(): void {
    try {
      const input = this.shadowRoot.querySelector('.date-input') as HTMLInputElement;
      if (!input) return;

      if (this.datePickerInstance && this.datePickerInstance.destroy) {
        this.datePickerInstance.destroy();
        this.datePickerInstance = null;
      }

      this.datePickerInstance = initFlatpickrInShadow(input, this.shadowRoot, {
        mode: 'range',
        dateFormat: 'Y-m-d',
        defaultDate: [this.startDate || new Date(), this.endDate || new Date()],
        onClose: (selectedDates: Date[]) => {
          if (selectedDates.length === 2) {
            const [start, end] = selectedDates;
            this.startDate = format(start, 'yyyy-MM-dd');
            this.endDate = format(end, 'yyyy-MM-dd');
            this.setupChart(this.config.symbol!);
            this.config.onDateChange?.(this.startDate, this.endDate);
          }
        },
      });
    } catch (err) {
      console.warn('Date picker init failed:', err);
    }
  }

  private async attachStyles(): Promise<void> {
    try {
      appendFlatpickrBaseStyles(this.shadowRoot);
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private async setupChart(symbol: string): Promise<void> {
    try {
      const response = await apiGet({
        method: 'POST',
        url: `${APIURL}/v1/company/fmp_esg_score?symbol=${encodeURIComponent(symbol)}&start_date=${this.startDate}&end_date=${this.endDate}`,
        body: {
          symbol,
          start_date: this.startDate,
          end_date: this.endDate,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch data');
      this.currentData = await response.json();

      const fromApi =
        (this.currentData as any)?.last_updated_at ??
        (this.currentData as any)?.data?.[0]?.created_at ??
        (this.currentData as any)?.data?.[0]?.accepted_date;
      this.lastFetchedAt = fromApi ? new Date(fromApi) : new Date();

      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error('Error fetching chart data:', err);
    }
  }

  private applyFilters(): void {
    const rows = Array.isArray(this.currentData?.data) ? [...this.currentData.data] : [];
    rows.sort((a: any, b: any) => {
      const dateA = new Date(a.date || a.accepted_date || 0).getTime();
      const dateB = new Date(b.date || b.accepted_date || 0).getTime();
      return dateB - dateA;
    });
    this.renderEsgScores(rows);
  }

  private renderEsgScores(rows: any[]): void {
    const container = this.shadowRoot.querySelector('#eventsContainer');
    const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;

    if (!container || !chartDv) return;

    const hasData = Array.isArray(rows) && rows.length > 0;

    if (hasData) {
      chartDv.setAttribute('data-testing-id', 'ESG Scores');
    } else {
      chartDv.removeAttribute('data-testing-id');
    }

    if (!rows.length) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    const formatScore = (val: any): string => {
      if (val === null || val === undefined) return '-';
      const num = typeof val === 'number' ? val : Number(val);
      if (Number.isNaN(num)) return String(val);
      return num.toFixed(2);
    };

    const formatDate = (val: any): string => {
      if (!val) return '-';
      const str = String(val);
      return str.includes('T') ? str.split('T')[0] : str;
    };

    let html = `
      <div class="eventsMain customGrid shareStructure--" style="--grid-template-columns: minmax(180px,1.8fr) minmax(90px,0.8fr) minmax(90px,0.8fr) minmax(100px,0.9fr) minmax(100px,0.9fr) minmax(100px,0.9fr) minmax(100px,0.9fr) minmax(100px,0.9fr) minmax(180px,1.6fr) minmax(110px,1fr) minmax(110px,1fr) minmax(90px,0.8fr); --grid-column-gap: 14px; min-width: 1550px;">
        <div class="item header">
          <p>Company</p>
          <p>Symbol</p>
          <p>Date</p>
          <p>Form Type</p>
          <p>ESG Score</p>
          <p>Environmental</p>
          <p>Social</p>
          <p>Governance</p>
          <p>Industry</p>
          <p>Accepted Date</p>
          <p>CIK</p>
          <p>Filing</p>
        </div>
    `;

    for (const row of rows) {
        const filingLink = row.url
        ? `<a href="${row.url}" target="_blank" rel="noopener noreferrer">
            ${new URL(row.url).hostname.replace('www.', '')}
          </a>`
        : '-';

      html += `
        <div class="item body">
          <p title="${row.company_name ?? '-'}">${row.company_name ?? '-'}</p>
          <p>${row.symbol ?? '-'}</p>
          <p>${formatDate(row.date)}</p>
          <p>${row.form_type ?? '-'}</p>
          <p class="stat left up"><span>${formatScore(row.esgscore)}</span></p>
          <p>${formatScore(row.environmental_score)}</p>
          <p>${formatScore(row.social_score)}</p>
          <p>${formatScore(row.governance_score)}</p>
          <p title="${row.industry ?? '-'}">${row.industry ?? '-'}</p>
          <p>${formatDate(row.accepted_date)}</p>
          <p>${row.cik ?? '-'}</p>
          <p class="stat">${filingLink}</p>
        </div>`;
    }

    html += `</div>`;
    container.innerHTML = html;
  }

  private formatLastUpdated(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
    };
    return date.toLocaleString('en-US', options).replace(',', '').replace(/\//g, '-') + ' EST';
  }

  private updateLastUpdatedTooltip(): void {
    const lastUpdated = this.lastFetchedAt ? this.formatLastUpdated(this.lastFetchedAt) : 'N/A';
    const tooltip = this.shadowRoot.querySelector('#lastUpdatedTooltip tooltip strong');
    if (tooltip) tooltip.innerHTML = lastUpdated.replace(' ', '<br>');
  }

  private updateDisplay(): void {
    this.updateLastUpdatedTooltip();
    if (!this.currentData) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.applyFilters();
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter((h) => h !== handler));
  }

  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;

    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

    this.updateDisplay();
  }

  public destroy(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
      this.widgetResizeObserver = null;
    }
    if (this.datePickerInstance && this.datePickerInstance.destroy) {
      this.datePickerInstance.destroy();
      this.datePickerInstance = null;
    }
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const evt: WidgetEvent = { type, data };
      handlers.forEach((h) => h(evt));
    }
  }

  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }
}

declare global {
  interface Window {
    TraderverseEsgScore: typeof TraderverseEsgScore;
  }
}
if (typeof window !== 'undefined') {
  window.TraderverseEsgScore = TraderverseEsgScore;
}
